/* ═══════════════════════════════════════════════════════════════════════════
   📝 services/drafts.js — what you typed in a chat and did not send, kept.
   (The web twin of the phone's stores/chatDrafts.ts.)

   • PER CHAT, PER ACCOUNT, in this browser (localStorage under the uid): leave
     a chat with words in the box and they are there when you come back — after
     switching chats, a reload, closing the tab.
   • THE CHAT LIST SHOWS IT: "Draft:" and the words in italics, instead of the
     last message (Sidebar's ChatRow).
   • Another tab of the same account sees the change (the 'storage' event).
   ═══════════════════════════════════════════════════════════════════════════ */
import { useSyncExternalStore } from 'react';

const KEY = 'allo_drafts_v1';
const MAX_LEN = 4000;
const MAX_DRAFTS = 300;

let owner = '';
let map = {};
let saveT = null;
const subs = new Set();
const emit = () => { subs.forEach((f) => { try { f(); } catch (_) {} }); };

export const draftWorthKeeping = (text) => typeof text === 'string' && text.trim().length > 0;
export const draftPreviewLine = (text) => String(text || '').replace(/\s+/g, ' ').trim().slice(0, 160);

function readDisk(uid) {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(`${KEY}:${uid}`) || '{}');
    const next = {};
    for (const [k, v] of Object.entries(parsed || {})) {
      if (v && draftWorthKeeping(v.text)) next[k] = { text: String(v.text).slice(0, MAX_LEN), at: Number(v.at) || 0 };
    }
    return next;
  } catch (_) { return {}; }
}

function persistNow() {
  if (!owner) return;
  try {
    const entries = Object.entries(map).sort((a, b) => b[1].at - a[1].at).slice(0, MAX_DRAFTS);
    window.localStorage.setItem(`${KEY}:${owner}`, JSON.stringify(Object.fromEntries(entries)));
  } catch (_) {}
}

/** This account's drafts (idempotent; '' forgets them in memory). */
export function bindDrafts(uid) {
  const u = String(uid || '');
  if (u === owner) return;
  if (saveT) { clearTimeout(saveT); saveT = null; persistNow(); }
  owner = u;
  map = u ? readDisk(u) : {};
  // told AFTER this render (it can be called while one is in progress — a row binding the account)
  setTimeout(emit, 0);
}

export function getDraft(chatId) {
  return chatId ? (map[chatId]?.text || '') : '';
}

/** Keep (or, when empty, forget) the draft for this chat. */
export function setDraft(chatId, text) {
  if (!chatId || !owner) return;
  const had = map[chatId];
  if (!draftWorthKeeping(text)) {
    if (!had) return;
    const next = { ...map };
    delete next[chatId];
    map = next;
  } else {
    const t = String(text).slice(0, MAX_LEN);
    if (had && had.text === t) return;
    map = { ...map, [chatId]: { text: t, at: Date.now() } };
  }
  emit();
  if (saveT) clearTimeout(saveT);
  saveT = setTimeout(() => { saveT = null; persistNow(); }, 300);
}

export function subscribeDrafts(fn) {
  subs.add(fn);
  return () => { subs.delete(fn); };
}

/** A row's draft — re-renders only that row when it changes. */
export function useDraft(chatId) {
  return useSyncExternalStore(subscribeDrafts, () => getDraft(chatId), () => getDraft(chatId));
}

if (typeof window !== 'undefined') {
  // closing the tab mid-debounce must not lose the last words
  window.addEventListener('beforeunload', () => { if (saveT) { clearTimeout(saveT); saveT = null; persistNow(); } });
  window.addEventListener('pagehide', () => { if (saveT) { clearTimeout(saveT); saveT = null; persistNow(); } });
  // another tab of this account typed or sent
  window.addEventListener('storage', (e) => {
    if (owner && e.key === `${KEY}:${owner}`) { map = readDisk(owner); emit(); }
  });
}
